import { Component, inject, NgZone, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '@app/header/header.component';
import { AuthService } from '@app/auth/auth.service';
import { AppService } from '@app/app.service';
import { Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs/operators';
import { LoadingOverlayComponent } from '@app/shared/loading-overlay/loading-overlay.component';
import { RouteHistoryService } from '@app/auth/route-history.service';
import { AuthActivityService } from '@app/auth/auth-activity.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    HeaderComponent,
    LoadingOverlayComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private titleService = inject(Title);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private appService = inject(AppService);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private routeHistory = inject(RouteHistoryService);
  private authActivityService = inject(AuthActivityService);

  hideHeader = false;

  constructor() {
    this.updateTitleOnRouteChange();
  }

  ngOnInit(): void {
    this.routeHistory.startTracking();
    this.authActivityService.start();

    window.addEventListener('storage', (event: StorageEvent) => {
      // Check for logout event
      if (event.key === 'auth_token' && !event.newValue) {
        this.ngZone.run(() => {
          this.router.navigate(['/login']);
          window.location.reload();
        });
      }

      // Check for login event from another tab
      if (event.key === 'login_event' && event.newValue) {
        this.ngZone.run(() => {
          this.appService.navigateToDefaultUrl();
          // Clear the login_event to prevent re-triggering
          localStorage.removeItem('login_event');
        });
      }
    });
  }

  public showHeader(): boolean {
    return this.authService.isAuthenticated()
      && !this.authService.isPendingLegalAcceptance()
      && !this.hideHeader;
  }

  public getStyle(): string {
    return this.hideHeader ? '' : 'main';
  }

  // Update Page Title Dynamically
  private updateTitleOnRouteChange() {
    const defaultTitle = 'RGN App';

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        // Update hideHeader based on route data
        this.hideHeader = route.snapshot.data['hideHeader'] === true;
        return route.snapshot.data['title'] || defaultTitle;
      })
    ).subscribe(title => {
      this.titleService.setTitle(title);
    });
  }
}